import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RolesDataRequestActionTypes, UserRoleFormData } from '../models/user-management';
import { getStatusMessage } from '../../utils/response-code-messages';
import Snackbar from '../../common/snackBar';
import get from 'lodash/get';
import { CONNECTIVITY_DASHBOARD_API_PATHS } from '../../utils/user-management';

const page_name = 'userRoleManagement';

export interface useCaseStates {
  isUserRoleLoading: boolean;
  userRole: Array<any>;
  userRoleTotalRowCount: number;
  userRolePageNumber: number;
  userRoleError: boolean;
  isAddRoleLoading: boolean;
  isAddRoleLoadingSuccess: boolean;
  isAddRoleError: boolean;
  isDeleteRoleSuccess: Boolean;
  isDeleteRoleError: Boolean;
  userRoleManagementMessage: string;
  isUserRoleAccessLoading: Boolean;
  userRoleAccess: Array<any>;
  isUserRoleAccessError: Boolean;
  isUpdateRoleAccessLoading: boolean;
  isUpdateRoleAccessSuccess: boolean;
  isUpdateRoleAccessError: boolean;
}

const initialState: useCaseStates = {
  isUserRoleLoading: false,
  userRole: [],
  userRoleTotalRowCount: 0,
  userRolePageNumber: 1,
  userRoleError: false,
  isAddRoleLoading: false,
  isAddRoleLoadingSuccess: false,
  isAddRoleError: false,
  isDeleteRoleSuccess: false,
  isDeleteRoleError: false,
  userRoleManagementMessage: '',
  isUserRoleAccessLoading: false,
  userRoleAccess: [],
  isUserRoleAccessError: false,
  isUpdateRoleAccessLoading: false,
  isUpdateRoleAccessSuccess: false,
  isUpdateRoleAccessError: false,
};

const UserRoleReducer = createSlice({
  name: page_name,
  initialState: initialState,
  reducers: {
    getRoleList: (state, action: RolesDataRequestActionTypes) => {
      state.isAddRoleLoadingSuccess = false;
      state.isDeleteRoleSuccess = false;
      state.isUserRoleLoading = true;
    },
    getRoleListResponse: (state, action) => {
      state.isUserRoleLoading = false;
      state.userRole = action.payload.res.data;
      state.userRoleTotalRowCount = action.payload.res.total_count;
      state.userRolePageNumber = action.payload.res.page;
    },
    getRoleListFailed: (state, action: any) => {
      state.isUserRoleLoading = false;
      state.userRoleError = true;
      state.userRole = [];

      const message = getStatusMessage(page_name, action.payload);
      state.userRoleManagementMessage = message;
      Snackbar.error(message);
    },
    addRoleRequest: (state) => {
      state.isUserRoleLoading = true;
      state.isAddRoleLoading = true;
      state.isAddRoleLoadingSuccess = false;
      state.isAddRoleError = false;
    },
    addRoleResponse: (state, action: any) => {
      state.isAddRoleLoading = false;
      state.isAddRoleLoadingSuccess = true;
      state.isUserRoleLoading = false;
      const message = getStatusMessage(page_name, action.payload);
      state.userRoleManagementMessage = message;
      Snackbar.success(message);
    },
    addRoleRequestFailed: (state, action: any) => {
      state.isAddRoleLoading = false;
      state.isAddRoleError = true;
      state.isUserRoleLoading = false;

      const message = getStatusMessage(page_name, action.payload);
      state.userRoleManagementMessage = message;
      Snackbar.error(message);
    },
    deleteMultipleRoleRequest: (state, action: PayloadAction<UserRoleFormData>) => {
      state.isUserRoleLoading = true;
      state.isDeleteRoleSuccess = false;
      state.isDeleteRoleError = false;
    },
    deleteMultipleRoleResponse: (state, action: PayloadAction<any>) => {
      state.isUserRoleLoading = false;
      state.isDeleteRoleSuccess = true;
      state.isDeleteRoleError = false;
      const message = getStatusMessage(page_name, action.payload);
      state.userRoleManagementMessage = message;
      Snackbar.success(message);
    },
    deleteMultipleRoleFailed: (state, action: any) => {
      state.isUserRoleLoading = false;
      state.isDeleteRoleSuccess = false;
      state.isDeleteRoleError = true;
      const message = getStatusMessage(page_name, action.payload);
      state.userRoleManagementMessage = message;
      Snackbar.error(message);
    },
    getRoleAccessList: (state, action: { payload: string }) => {
      state.isUpdateRoleAccessSuccess = false;
      state.isUserRoleAccessLoading = true;
    },
    getRoleAccessListResponse: (state, action) => {
      state.isUserRoleAccessLoading = false;

      const getAttributes = (attrs: any) => {
        const filteredAttributes: any = {};
        const connectivityDashboardAttributes: any = {};

        Object.keys(attrs).forEach((attr: string) => {
          if (CONNECTIVITY_DASHBOARD_API_PATHS.includes(attr)) {
            connectivityDashboardAttributes[attr] = attrs[attr][0];
            filteredAttributes['things/connectivity_dashboard'] = ['0'];
          } else {
            if (process.env.REACT_APP_API_THINGS_GATEWAYS === attr) {
              connectivityDashboardAttributes[attr] = attrs[attr][0];
            }
            filteredAttributes[attr] = attrs[attr];
          }
        });
        return {
          ...filteredAttributes,
          'things/connectivity_dashboard': [
            ...CONNECTIVITY_DASHBOARD_API_PATHS,
            // process.env.REACT_APP_API_THINGS_GATEWAYS,
          ].every((acc: any) => connectivityDashboardAttributes[acc] === '1')
            ? ['1']
            : ['0'],
        };
      };

      state.userRoleAccess = {
        ...action.payload.res,
        attributes: getAttributes(get(action, 'payload.res.attributes', {})),
      };
    },
    getRoleAccessListFailed: (state, action: any) => {
      state.isUserRoleAccessLoading = false;
      state.userRoleError = true;
      state.userRoleAccess = [];
      const message = getStatusMessage(page_name, action.payload);
      state.userRoleManagementMessage = message;
      Snackbar.error(message);
    },
    updateRoleAccessRequest: (state) => {
      state.isUserRoleAccessLoading = true;
      state.isUpdateRoleAccessLoading = true;
      state.isUpdateRoleAccessSuccess = false;
      state.isUpdateRoleAccessError = false;
    },
    updateRoleAccessResponse: (state, action: any) => {
      state.isUpdateRoleAccessLoading = false;
      state.isUpdateRoleAccessSuccess = true;
      state.isUserRoleAccessLoading = false;
      const message = getStatusMessage(page_name, action.payload);
      state.userRoleManagementMessage = message;
      Snackbar.success(message);
    },
    updateRoleAccessFailed: (state, action: any) => {
      state.isUpdateRoleAccessLoading = false;
      state.isUpdateRoleAccessError = true;
      state.isUserRoleAccessLoading = false;
      const message = getStatusMessage(page_name, action.payload);
      state.userRoleManagementMessage = message;
      Snackbar.error(message);
    },
    resetLoadingState: (state) => {
      state.isAddRoleLoadingSuccess = false;
      state.isAddRoleError = false;
      state.isUserRoleLoading = false;
      state.isDeleteRoleSuccess = false;
      state.isDeleteRoleError = false;
      state.userRoleManagementMessage = '';
      state.userRoleError = false;
      state.isUserRoleAccessError = false;
      state.isUpdateRoleAccessSuccess = false;
      state.isUpdateRoleAccessError = false;
    },
    resetUserRoleAccess: (state) => {
      state.userRoleAccess = [];
    },
    resetUserRoleData: (state) => {
      state.userRole = [];
      state.userRoleTotalRowCount = 0;
      state.userRolePageNumber = 1;
    },
  },
});

export const userRoleActions = UserRoleReducer.actions;
export default UserRoleReducer;
